import { ChangeEvent, useEffect } from 'react';
import clsx from 'clsx';

import { FormWrapper } from '@/stories/components/Form/Form';
import { Input } from '@/stories/components/Form/Input/Input';
import { Row } from '@/stories/organisms/Grid/Row';
import { Button } from '@/stories/atoms/Button/Button';
import { Icon, type IconType } from '@/stories/atoms/Icon/Icon';
import { List } from '@/stories/components/Form/List/List';
import {type LocalityType, LocalitySearch } from '@/stories/components/Form/LocalitySearch/LocalitySearch';

import { DropFilter } from './DropFilter/DropFilter';
import styles from './search-module.module.css'
import tbStyles from '../Filters/TypeBien/type-biens.module.css'
import { useSearchModuleStore } from './useSearchModuleStore';

export const SearchPanel = ({
  context,
  redirectUrl,
  locality: defaultLocality,
}: {
  context: 'buy'| 'rent',
  redirectUrl: string,
  locality?: LocalityType,
}) => {
  const { localities, pmax, tb, radiusValue, setData} = useSearchModuleStore()

  useEffect(() => {
    if (defaultLocality) setData({localities: [defaultLocality]})
  }, [setData, defaultLocality])

  const handlePmaxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setData({pmax: value})
  }

  return (
    <div>
      <FormWrapper action={redirectUrl}>
        <Row sDirectionCol>
          <LocalitySearch
            localityTypes={['commune', 'departement', 'arrondissement']}
            label='Ou cherchez-vous ?'
            hideLabel
            localities={localities}
            variant='secondary'
            icon='location-dot'
            radiusEnabled
            radiusValue={radiusValue}
            multiple
            handleRadiusChange={(e) => {
              const { value } = e.target as HTMLInputElement
              setData({radiusValue: value === '0' ? undefined : parseInt(value) * 1000})
            }}
            setLocalities={(loc) => {
              const singleCommune = loc.length === 1 && loc[0].type === 'commune'
              setData({
                localities: loc,
                // les arrondissements sont filtrés par contour, sans rayon
                radiusValue: singleCommune ? 5000 : (loc.length > 1 && loc[0].type !== 'arrondissement' ? 'all' : undefined)
              })
            }}/>
          <DropFilter
            hideLabel
            title={
              tb && tb.length > 0 ? `${tb[0]}${tb.length >= 2 ? ` +${tb.length -1}` : ''}` : "Tous les biens"}
            icon="house"
            label="Type de biens"
            variant='secondary'
            className={clsx(styles.field)}
            panelClassName={tbStyles.tbPanel}>
            <List
              values={tb}
              setValues={value => setData({tb: value})}
              name="tb"
              label="Type de bien"
              hideLabel
              item='TypeBien'
              inline
              list={[
                {label: 'Maison', icon: 'house'},
                {label: 'Appartement', icon: 'apartment'},
                {label: 'Immeuble', icon: 'building'},
                {label: 'Commerce / Industrie', icon: 'gear'},
                {label: 'Parking / Box', icon: 'car-garage'},
                ...(context === 'buy' ? [{label: 'Terrain', icon: 'tree' as IconType}] : []),
              ]}
              variant='secondary'
              className={clsx(styles.field, tbStyles.tbPanel)}
              />
          </DropFilter>
          <Input
            onChange={handlePmaxChange}
            value={pmax?.toString() ?? ''}
            {...(pmax && pmax.toString().trim() !== '' ? {name: 'pmax'} : {})}
            variant='secondary'
            icon='euro'
            type='number'
            min={0}
            step={context === 'buy' ? 5000 : 100}
            label='Budget max'
            hideLabel
            placeholder='Budget max'
            className={styles.field}/>
          <Button className={styles.submit} type='submit' rounded><><span className='sr-only-md'>Rechercher</span> <Icon icon='search'/></></Button>
        </Row>
      </FormWrapper>
    </div>
  )
};
